import { mergeActivityMaps } from './activityService';

export const SYNC_META_KEY = 'crm_sync_meta';
export const SYNC_CONFLICT_EVENT = 'crm-sync-conflict';

/** @typedef {{ updatedAt: string, source?: 'local'|'remote' }} SyncMetaEntry */

export function getSyncMeta() {
  try {
    const raw = localStorage.getItem(SYNC_META_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function setSyncMetaEntry(key, entry) {
  const meta = getSyncMeta();
  meta[key] = { ...entry, updatedAt: entry.updatedAt || new Date().toISOString() };
  try {
    localStorage.setItem(SYNC_META_KEY, JSON.stringify(meta));
  } catch { /* ignore */ }
}

export function touchLocalSyncMeta(key) {
  setSyncMetaEntry(key, { updatedAt: new Date().toISOString(), source: 'local' });
}

function parseTs(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/* ── Call Sessions: Merge pro Session-ID statt Last-Write-Wins aufs Array ──
   Gelöschte Sessions bleiben als Tombstones ({ id, deleted, updatedAt }) im
   Array, damit die Löschung auf andere Geräte propagiert. */
const SESSION_TOMBSTONE_TTL_MS = 30 * 24 * 3600 * 1000;

export function mergeSessionArrays(localArr, remoteArr) {
  const byId = new Map();
  for (const s of [...(Array.isArray(localArr) ? localArr : []), ...(Array.isArray(remoteArr) ? remoteArr : [])]) {
    if (!s || s.id == null) continue;
    const prev = byId.get(s.id);
    if (!prev) { byId.set(s.id, s); continue; }
    const a = parseTs(prev.updatedAt || prev.createdAt);
    const b = parseTs(s.updatedAt || s.createdAt);
    if (b > a) byId.set(s.id, s);
  }
  const cutoff = Date.now() - SESSION_TOMBSTONE_TTL_MS;
  return [...byId.values()]
    .filter(s => !s.deleted || parseTs(s.updatedAt) > cutoff)
    .sort((x, y) => parseTs(y.updatedAt || y.createdAt) - parseTs(x.updatedAt || x.createdAt));
}

function readLocalRaw(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function dispatchConflict(key, message) {
  try {
    window.dispatchEvent(new CustomEvent(SYNC_CONFLICT_EVENT, {
      detail: { key, message },
    }));
  } catch { /* ignore */ }
}

/**
 * Entscheidet, ob Remote- oder Local-Wert beim Hydrate gewinnt.
 * @returns {{ apply: boolean, value: *, conflict?: boolean, message?: string }}
 */
export function resolveHydrateValue(key, remoteValue, remoteUpdatedAt) {
  if (key === 'crm_call_sessions' && Array.isArray(remoteValue)) {
    let local = [];
    try {
      const raw = readLocalRaw(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) local = parsed;
      }
    } catch { /* keep local empty */ }
    const merged = mergeSessionArrays(local, remoteValue);
    touchLocalSyncMeta(key);
    return { apply: true, value: merged, conflict: false };
  }

  if (key === 'crm_activities' && remoteValue && typeof remoteValue === 'object' && !Array.isArray(remoteValue)) {
    let local = {};
    try {
      const raw = readLocalRaw('crm_activities');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) local = parsed;
      }
    } catch { /* keep local empty */ }
    const merged = mergeActivityMaps(local, remoteValue);
    touchLocalSyncMeta('crm_activities');
    return { apply: true, value: merged, conflict: false };
  }

  const localRaw = readLocalRaw(key);
  const meta = getSyncMeta()[key];
  const localTs = parseTs(meta?.updatedAt);
  const remoteTs = parseTs(remoteUpdatedAt);

  if (localRaw == null) {
    if (remoteUpdatedAt) setSyncMetaEntry(key, { updatedAt: remoteUpdatedAt, source: 'remote' });
    return { apply: true, value: remoteValue, conflict: false };
  }

  let localValue;
  try {
    localValue = JSON.parse(localRaw);
  } catch {
    if (remoteUpdatedAt) setSyncMetaEntry(key, { updatedAt: remoteUpdatedAt, source: 'remote' });
    return { apply: true, value: remoteValue, conflict: false };
  }

  if (!remoteUpdatedAt) {
    return { apply: true, value: remoteValue, conflict: false };
  }

  if (remoteTs > localTs) {
    setSyncMetaEntry(key, { updatedAt: remoteUpdatedAt, source: 'remote' });
    return { apply: true, value: remoteValue, conflict: false };
  }

  if (localTs > remoteTs) {
    return { apply: false, value: localValue, conflict: false };
  }

  setSyncMetaEntry(key, { updatedAt: remoteUpdatedAt, source: 'remote' });
  return { apply: true, value: remoteValue, conflict: false };
}

/** Wendet Hydrate-Ergebnisse an; liefert Konflikt-Liste für UI. */
export function applyHydrateRows(rows) {
  /** @type {{ key: string, message: string }[]} */
  const conflicts = [];

  for (const row of rows || []) {
    const { key, value, updated_at: updatedAt } = row;
    if (value === null || value === undefined || !key) continue;

    const result = resolveHydrateValue(key, value, updatedAt);
    if (result.conflict && result.message) {
      conflicts.push({ key, message: result.message });
      continue;
    }
    if (!result.apply) continue;

    try {
      localStorage.setItem(key, JSON.stringify(result.value));
      if (updatedAt) {
        setSyncMetaEntry(key, { updatedAt, source: 'remote' });
      }
    } catch { /* ignore */ }
  }

  return conflicts;
}
